// =============================================================================
// GlobalAvatarOverlay — Floating avatar above ALL content app-wide
// Independent of any page. Draggable, resizable, dockable.
// Connects to the Voice Engine via r2:tts:* custom events.
// CSS-based lip-sync driven by Web Audio API amplitude analysis.
// =============================================================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Minimize2, Maximize2, GripVertical, User } from 'lucide-react';
import { useAvatarStore, type DockPosition } from '../../stores/avatar.store.js';

// ── Dock position calculator ──────────────────────────────────────────────────
function getDockCoords(dock: DockPosition, w: number, h: number): { x: number; y: number } {
  const margin = 24;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  switch (dock) {
    case 'bottom-right': return { x: vw - w - margin,     y: vh - h - margin };
    case 'bottom-left':  return { x: margin,               y: vh - h - margin };
    case 'top-right':    return { x: vw - w - margin,     y: margin + 56 }; // below top bar
    case 'top-left':     return { x: margin,               y: margin + 56 };
    default:             return { x: vw - w - margin,     y: vh - h - margin };
  }
}

// ── Animated avatar canvas ────────────────────────────────────────────────────
interface AvatarCanvasProps {
  imageUrl: string;
  isSpeaking: boolean;
  amplitude: number;
  bgMode: string;
  width: number;
  height: number;
}

function AvatarCanvas({ imageUrl, isSpeaking, amplitude, bgMode, width, height }: AvatarCanvasProps) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [blinkState, setBlinkState] = useState(1); // 1=open, 0=closed
  const [headY, setHeadY] = useState(0);
  const [headRotate, setHeadRotate] = useState(0);
  const animRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const blinkRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Preload image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    imgRef.current = img;
  }, [imageUrl]);

  // Idle head sway
  useEffect(() => {
    let t = 0;
    animRef.current = setInterval(() => {
      t += 0.04;
      setHeadY(Math.sin(t * 0.8) * 3);
      setHeadRotate(Math.sin(t * 0.5) * 1.2);
    }, 50);
    return () => { if (animRef.current) clearInterval(animRef.current); };
  }, []);

  // Eye blink every 3–5 seconds
  useEffect(() => {
    const scheduleBlink = () => {
      const delay = 3000 + Math.random() * 2000;
      blinkRef.current = setTimeout(() => {
        setBlinkState(0);
        setTimeout(() => setBlinkState(1), 120);
        scheduleBlink();
      }, delay);
    };
    scheduleBlink();
    return () => { if (blinkRef.current) clearTimeout(blinkRef.current); };
  }, []);

  // Jaw opening based on amplitude (lip-sync)
  const jawOpen = isSpeaking ? amplitude * 0.35 : 0;

  // Background color for chroma modes
  const bgColor = bgMode === 'green-screen' ? '#00ff00'
    : bgMode === 'blue-screen'  ? '#0000ff'
    : bgMode === 'transparent'  ? 'transparent'
    : 'rgba(11,20,38,0.85)';

  return (
    <div
      style={{
        width, height,
        background: bgColor,
        borderRadius: bgMode === 'transparent' ? 0 : 12,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Avatar image with CSS-driven animations */}
      <div style={{
        position: 'relative',
        transform: `translateY(${headY}px) rotate(${headRotate}deg)`,
        transition: 'transform 0.1s ease-out',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <img
          src={imageUrl}
          alt="avatar"
          crossOrigin="anonymous"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
            display: 'block',
          }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />

        {/* Eye blink overlay — covers upper face with skin-tone semi-transparent layer */}
        {blinkState === 0 && (
          <div style={{
            position: 'absolute',
            top: '22%',
            left: '15%',
            width: '70%',
            height: '10%',
            background: 'rgba(180, 130, 90, 0.85)',
            borderRadius: 4,
            transition: 'opacity 0.06s',
          }} />
        )}

        {/* Lip-sync jaw indicator — subtle brightness pulse on lower face */}
        {jawOpen > 0.05 && (
          <div style={{
            position: 'absolute',
            bottom: '12%',
            left: '30%',
            width: '40%',
            height: `${jawOpen * 100}%`,
            maxHeight: '12%',
            background: 'rgba(0,0,0,0.25)',
            borderRadius: '0 0 50% 50%',
            transition: 'height 0.04s, opacity 0.04s',
          }} />
        )}

        {/* Speaking glow ring */}
        {isSpeaking && (
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            boxShadow: `0 0 ${12 + amplitude * 20}px rgba(37,181,168,${0.15 + amplitude * 0.3})`,
            pointerEvents: 'none',
            transition: 'box-shadow 0.05s',
          }} />
        )}
      </div>

      {/* Status indicator dot */}
      <div style={{
        position: 'absolute',
        bottom: 8,
        right: 8,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: isSpeaking ? '#25b5a8' : '#334155',
        boxShadow: isSpeaking ? '0 0 6px rgba(37,181,168,0.9)' : 'none',
        transition: 'all 0.2s',
      }} />
    </div>
  );
}

// ── Placeholder when no avatar is assigned ────────────────────────────────────
function AvatarPlaceholder({ width, height, isSpeaking }: { width: number; height: number; isSpeaking: boolean }) {
  return (
    <div style={{
      width, height,
      background: 'rgba(11,20,38,0.92)',
      borderRadius: 12,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      border: `1px solid ${isSpeaking ? 'rgba(37,181,168,0.5)' : 'rgba(37,60,90,0.5)'}`,
      boxShadow: isSpeaking ? '0 0 20px rgba(37,181,168,0.15)' : 'none',
      transition: 'all 0.3s',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: 'rgba(37,181,168,0.1)',
        border: '2px solid rgba(37,181,168,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <User className="h-7 w-7" style={{ color: 'rgba(37,181,168,0.6)' }} />
      </div>
      <div style={{ fontSize: 10, color: '#475569', textAlign: 'center', letterSpacing: '0.05em' }}>
        No avatar assigned
      </div>
      {isSpeaking && (
        <div style={{ display: 'flex', gap: 3 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{
              width: 4, height: 12, borderRadius: 2, background: '#25b5a8',
              animation: `r2-avatar-bar 0.6s ease-in-out ${i * 0.1}s infinite alternate`,
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Web Audio amplitude analyser ──────────────────────────────────────────────
function useAudioAmplitude(onAmplitude: (a: number) => void, onSpeakingChange: (s: boolean) => void) {
  const audioCtxRef  = useRef<AudioContext | null>(null);
  const analyserRef  = useRef<AnalyserNode | null>(null);
  const srcRef       = useRef<MediaElementAudioSourceNode | null>(null);
  const rafRef       = useRef<number>(0);
  const currentElRef = useRef<HTMLAudioElement | null>(null);

  const attach = useCallback((el: HTMLAudioElement) => {
    if (currentElRef.current === el) return;
    // Clean up previous
    if (srcRef.current) { try { srcRef.current.disconnect(); } catch {} }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close();
    }
    cancelAnimationFrame(rafRef.current);

    currentElRef.current = el;
    const ctx      = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    const src = ctx.createMediaElementSource(el);
    src.connect(analyser);
    analyser.connect(ctx.destination);

    audioCtxRef.current = ctx;
    analyserRef.current = analyser;
    srcRef.current      = src;

    const buf = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = ((buf[i] ?? 128) - 128) / 128;
        sum += (v ?? 0) * (v ?? 0);
      }
      const rms = Math.sqrt(sum / buf.length);
      onAmplitude(Math.min(1, rms * 4));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [onAmplitude]);

  const detach = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    onAmplitude(0);
    onSpeakingChange(false);
    currentElRef.current = null;
  }, [onAmplitude, onSpeakingChange]);

  useEffect(() => {
    const onStart = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.audioElement instanceof HTMLAudioElement) {
        attach(detail.audioElement);
        onSpeakingChange(true);
      } else {
        // Browser TTS — no audio element, just pulse
        onSpeakingChange(true);
        onAmplitude(0.5);
      }
    };
    const onEnd = () => { detach(); };

    window.addEventListener('r2:tts:start', onStart);
    window.addEventListener('r2:tts:end',   onEnd);
    return () => {
      window.removeEventListener('r2:tts:start', onStart);
      window.removeEventListener('r2:tts:end',   onEnd);
      cancelAnimationFrame(rafRef.current);
    };
  }, [attach, detach, onAmplitude, onSpeakingChange]);
}

// ── Resize handles ────────────────────────────────────────────────────────────
const DOCK_BUTTONS: { id: DockPosition; label: string }[] = [
  { id: 'bottom-right', label: 'BR' },
  { id: 'bottom-left',  label: 'BL' },
  { id: 'top-right',    label: 'TR' },
  { id: 'top-left',     label: 'TL' },
];

// ── Main Overlay Component ────────────────────────────────────────────────────
export function GlobalAvatarOverlay(): JSX.Element | null {
  const store = useAvatarStore();
  const {
    isOverlayVisible, activeAvatarId, avatars,
    position, size, dockPosition, bgMode,
    isSpeaking, amplitude,
    hideOverlay, setPosition, setSize, setDockPosition,
    setSpeaking, setAmplitude,
  } = store;

  const activeAvatar = avatars.find((a) => a.id === activeAvatarId) ?? null;
  const overlayRef   = useRef<HTMLDivElement>(null);
  const dragRef      = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);
  const resizeRef    = useRef<{ startX: number; startY: number; originW: number; originH: number } | null>(null);
  const [isMinimised, setIsMinimised] = useState(false);
  const [isDragging,  setIsDragging]  = useState(false);

  // Audio amplitude → speaking state
  useAudioAmplitude(
    (a) => setAmplitude(a),
    (s) => setSpeaking(s, 0),
  );

  // Snap to dock on mount / dock change
  useEffect(() => {
    const coords = getDockCoords(dockPosition, size.width, size.height);
    if (dockPosition !== 'custom') setPosition(coords);
  }, [dockPosition, size.width, size.height]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drag ──────────────────────────────────────────────────────────────────
  const onDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: position.x, originY: position.y };
    setIsDragging(true);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPosition({ x: dragRef.current.originX + dx, y: dragRef.current.originY + dy });
    };
    const onUp = () => {
      if (dragRef.current) { dragRef.current = null; setIsDragging(false); }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [setPosition]);

  // ── Resize ────────────────────────────────────────────────────────────────
  const onResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, originW: size.width, originH: size.height };
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;
      setSize({
        width:  Math.max(120, resizeRef.current.originW + dx),
        height: Math.max(160, resizeRef.current.originH + dy),
      });
    };
    const onUp = () => { resizeRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [setSize]);

  if (!isOverlayVisible) return null;

  const displayH = isMinimised ? 48 : size.height;

  return (
    <>
      <style>{`
        @keyframes r2-avatar-bar {
          from { transform: scaleY(0.4); opacity: 0.5; }
          to   { transform: scaleY(1.0); opacity: 1.0; }
        }
      `}</style>

      <div
        ref={overlayRef}
        data-testid="global-avatar-overlay"
        style={{
          position:  'fixed',
          left:       position.x,
          top:        position.y,
          width:      size.width,
          height:     displayH,
          zIndex:     9999,
          userSelect: 'none',
          transition: isDragging ? 'none' : 'box-shadow 0.3s',
          filter:     isDragging
            ? 'drop-shadow(0 8px 32px rgba(37,181,168,0.3))'
            : 'drop-shadow(0 4px 16px rgba(0,0,0,0.5))',
        }}
      >
        {/* ── Top control bar ──────────────────────────────────────────────── */}
        <div
          onMouseDown={onDragStart}
          style={{
            height:     36,
            borderRadius: isMinimised ? 10 : '10px 10px 0 0',
            background: 'rgba(8,16,36,0.95)',
            border:     '1px solid rgba(37,181,168,0.25)',
            borderBottom: isMinimised ? undefined : '1px solid rgba(37,181,168,0.12)',
            display:    'flex',
            alignItems: 'center',
            gap:         6,
            padding:    '0 10px',
            cursor:     isDragging ? 'grabbing' : 'grab',
          }}
        >
          <GripVertical className="h-3.5 w-3.5 shrink-0" style={{ color: '#334155' }} />

          {/* Speaking indicator */}
          <div style={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            background: isSpeaking ? '#25b5a8' : '#334155',
            boxShadow: isSpeaking ? '0 0 5px rgba(37,181,168,0.8)' : 'none',
            transition: 'all 0.15s',
          }} />

          <span style={{ flex: 1, fontSize: 10, color: '#475569', letterSpacing: '0.06em',
            fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeAvatar ? activeAvatar.name : 'R2 Avatar'}
          </span>

          {/* Dock buttons */}
          <div style={{ display: 'flex', gap: 2 }}>
            {DOCK_BUTTONS.map((d) => (
              <button
                key={d.id}
                data-testid={`avatar-dock-${d.id}`}
                onClick={() => setDockPosition(d.id)}
                title={`Dock: ${d.label}`}
                style={{
                  width: 18, height: 18, borderRadius: 3, border: 'none', cursor: 'pointer',
                  background:  dockPosition === d.id ? 'rgba(37,181,168,0.25)' : 'rgba(255,255,255,0.04)',
                  color:       dockPosition === d.id ? '#5eead4' : '#475569',
                  fontSize:    8, fontWeight: 700, letterSpacing: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {d.label}
              </button>
            ))}
          </div>

          <button
            data-testid="avatar-minimise-btn"
            onClick={() => setIsMinimised((v) => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 2,
              display: 'flex', alignItems: 'center' }}
          >
            {isMinimised
              ? <Maximize2 className="h-3 w-3" />
              : <Minimize2 className="h-3 w-3" />
            }
          </button>
          <button
            data-testid="avatar-close-btn"
            onClick={hideOverlay}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 2,
              display: 'flex', alignItems: 'center' }}
          >
            <X className="h-3 w-3" />
          </button>
        </div>

        {/* ── Avatar body ──────────────────────────────────────────────────── */}
        {!isMinimised && (
          <div style={{ position: 'relative', border: '1px solid rgba(37,181,168,0.18)',
            borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
            {activeAvatar ? (
              <AvatarCanvas
                imageUrl={activeAvatar.processedImageUrl}
                isSpeaking={isSpeaking}
                amplitude={amplitude}
                bgMode={bgMode}
                width={size.width}
                height={size.height - 36}
              />
            ) : (
              <AvatarPlaceholder
                width={size.width}
                height={size.height - 36}
                isSpeaking={isSpeaking}
              />
            )}

            {/* ── Resize handle ──────────────────────────────────────────── */}
            <div
              data-testid="avatar-resize-handle"
              onMouseDown={onResizeStart}
              style={{
                position: 'absolute', bottom: 2, right: 2,
                width: 16, height: 16, cursor: 'se-resize',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
                color: '#334155',
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <path d="M8 2 L10 2 L10 10 L2 10 L2 8 L8 8 Z" opacity="0.5" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
